import { ReactNode, useState } from 'react';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { focusSurface } from '@/components/ui/focusStyles';
import { ICONS } from '@/constants/icons';
import { useTranslation } from 'react-i18next';
import { DashboardCollapseButton } from './DashboardCollapseButton';

/**
 * DashboardSection Component
 *
 * A collapsible, draggable section wrapper for dashboard widgets.
 *
 * Features:
 * - Sits inside a draggable dashboard row
 * - Collapsible with smooth animation
 * - Hideable with close button
 * - Visual feedback when dragging
 * - Optional icon in header
 * - Full keyboard accessibility
 *
 * Design:
 * - Card layout with header and content areas
 * - Header: title icon/tooltip + collapse button + hide button
 * - Content: smooth collapse animation with overflow handling
 * - Dragging state: opacity + ring indicator
 *
 * @example
 * ```tsx
 * // Basic usage
 * <DashboardSection
 *   id="recent-posts"
 *   title="Recent Posts"
 *   icon={ICONS.FILE}
 * >
 *   <PostList />
 * </DashboardSection>
 * ```
 *
 * @example
 * ```tsx
 * // Controlled collapse state
 * const [collapsed, setCollapsed] = useState(false);
 *
 * <DashboardSection
 *   id="my-section"
 *   title="My Section"
 *   collapsed={collapsed}
 *   onToggleCollapse={() => setCollapsed(!collapsed)}
 * >
 *   <Content />
 * </DashboardSection>
 * ```
 */

export interface DashboardSectionProps {
  /**
   * Unique section identifier
   */
  id: string;

  /**
   * Section title
   */
  title: string;

  /**
   * Optional icon name from ICONS constant
   */
  icon?: string;

  /**
   * Section content
   */
  children: ReactNode;

  /**
   * Collapsed state
   */
  collapsed?: boolean;

  /**
   * Callback when collapse button clicked
   */
  onToggleCollapse?: () => void;

  /**
   * Callback when hide button clicked
   */
  onHide?: () => void;

  /**
   * Whether section is currently being dragged
   */
  isDragging?: boolean;

  /**
   * Additional CSS classes for wrapper
   */
  className?: string;

  /**
   * Optional actions rendered in the header (between the title icon and collapse/hide buttons)
   */
  headerActions?: ReactNode;
}

export const DashboardSection = ({
  id,
  title,
  icon,
  children,
  collapsed = false,
  onToggleCollapse,
  onHide,
  isDragging = false,
  className = '',
  headerActions,
}: DashboardSectionProps) => {
  const { t } = useTranslation('common');
  const [titleTooltipOpen, setTitleTooltipOpen] = useState(false);

  return (
    <div
      className={`
        bg-white rounded-xl shadow-card
        transition-all duration-200
        ${isDragging ? 'opacity-50 ring-2 ring-primary-500' : ''}
        ${className}
      `}
      role="region"
      aria-labelledby={`section-title-${id}`}
    >
      {/* Header */}
      <div className={`flex items-center justify-between p-4 ${collapsed ? '' : 'border-b border-gray-200'}`}>
        {/* Left: the icon that reveals the title. */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* The title remains the accessible region heading; its icon opens the visible label. */}
          <div className="flex-1 flex items-center justify-start min-w-0">
            {icon ? (
              <>
                <h3 id={`section-title-${id}`} className="sr-only text-lg font-bold text-gray-900">{title}</h3>
                <Tooltip content={title} side="bottom" open={titleTooltipOpen} onOpenChange={setTitleTooltipOpen}>
                  <button
                    type="button"
                    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-700 ${focusSurface}`}
                    aria-label={title}
                    onPointerDown={(event) => event.stopPropagation()}
                    onPointerUp={(event) => {
                      if (event.pointerType !== 'mouse') setTitleTooltipOpen((wasOpen) => !wasOpen);
                    }}
                  >
                    <Icon name={icon} size="md" ariaHidden />
                  </button>
                </Tooltip>
              </>
            ) : (
              <h3 id={`section-title-${id}`} className="text-lg font-bold text-gray-900">{title}</h3>
            )}
          </div>
        </div>

        {/* Right: Header Actions + Collapse + Hide Buttons */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {headerActions}
          {/* Collapse Button */}
          {onToggleCollapse && (
            <DashboardCollapseButton collapsed={collapsed} onClick={onToggleCollapse} />
          )}

          {/* Hide Button */}
          {onHide && (
            <button
              type="button"
              onClick={onHide}
              className={`p-2 rounded-md hover:bg-gray-100 transition-colors ${focusSurface}`}
              aria-label={t('hide_section', { defaultValue: 'Hide section' })}
            >
              <Icon name={ICONS.CLOSE} size="sm" color="text-gray-600" ariaHidden />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div
        className={`
          grid transition-[grid-template-rows] duration-200 ease-out min-h-0
          ${collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr] flex-1'}
        `}
      >
        <div className="overflow-hidden min-h-0">
          <div className="p-6 h-full">{children}</div>
        </div>
      </div>
    </div>
  );
};
